import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Select } from 'vopay';

import { Toggle } from 'UI';

import styles from './renderBillingField.module.scss';

const MONTH_IDS = [0, 1, 2, 3, 4, 5, 6] as const;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GetFormikData = <K>(options: object) => {
  name: string;
  value: K;
  onChange: React.ChangeEventHandler<any>;
  onBlur: React.FocusEventHandler<any>;
  disabled: boolean;
  errorMsg?: string;
};

export function useBillingFieldRenderer(currency: string) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'R1AccountCreation.Billing.PackageDetails',
  });

  const monthOptions = useMemo(
    () => MONTH_IDS.map((n) => ({ id: String(n), label: t('monthOption', { count: n }) })),
    [t],
  );

  return useCallback(
    (
      type: string,
      getFormikData: GetFormikData,
      selectOptions?: { value: string; label: string }[],
    ): React.JSX.Element => {
      switch (type) {
        case 'amount': {
          const fd = getFormikData<string>({});
          return (
            <TextField
              variant="outlineShadow"
              preset="currency"
              errorFitContent
              elementLeft={<span className={styles.adornment}>{currency}</span>}
              {...fd}
            />
          );
        }

        case 'percent': {
          const fd = getFormikData<string>({});
          return (
            <TextField
              variant="outlineShadow"
              preset="numeric"
              errorFitContent
              elementLeft={<span className={styles.adornment}>%</span>}
              {...fd}
            />
          );
        }

        case 'month': {
          const fd = getFormikData<string>({});
          return (
            <div className={styles.fullWidth}>
              <Select
                version="2"
                options={monthOptions}
                optionValue={(o) => o.id}
                optionLabel={(o) => o.label}
                {...fd}
              />
            </div>
          );
        }

        case 'select': {
          const fd = getFormikData<string>({});
          return (
            <div className={styles.fullWidth}>
              <Select
                version="2"
                options={selectOptions ?? []}
                optionValue={(o) => o.value}
                optionLabel={(o) => o.label}
                renderSelectedValue={(_option, { value, label, Placeholder }) =>
                  value ? <span className={styles.selectedValue}>{label}</span> : <Placeholder />
                }
                {...fd}
              />
            </div>
          );
        }

        case 'numeric': {
          const fd = getFormikData<string>({});
          return <TextField variant="outlineShadow" preset="numericWholePositive" errorFitContent {...fd} />;
        }

        case 'boolean': {
          const { name, value, onChange } = getFormikData<boolean>({});
          return <Toggle name={name} checked={Boolean(value)} onChange={onChange} />;
        }

        default: {
          const fd = getFormikData<string>({});
          return <TextField variant="outlineShadow" errorFitContent {...fd} />;
        }
      }
    },
    [currency, monthOptions],
  );
}
